import { requireActiveOrg, getOrganizationByRepo, OrganizationInfo } from '../lib/config'
import { FlightDeskAPI } from '../lib/api'
import { detectGitRepo } from '../lib/git'
import { spawn } from 'node:child_process'
import { mkdtempSync, readFileSync, rmSync } from 'node:fs'
import { tmpdir } from 'node:os'
import { join } from 'node:path'

interface RegisterOptions {
  project?: string
  subproject?: string
  viewUrl?: string
  teleportId?: string
  title?: string
  description?: string
  prompt?: string
}

export async function registerCommand(
  taskId: string | undefined,
  options: RegisterOptions
): Promise<void> {
  const { config, org } = requireActiveOrg()
  const api = FlightDeskAPI.fromConfig(config, org)

  try {
    // Auto-detect project if not provided via --project option
    let projectId = options.project
    if (!projectId) {
      projectId = await autoDetectProject(api, org)
    }

    // Resolve session info from --prompt flag, piped stdin, or explicit flags
    let viewUrl = options.viewUrl
    let teleportId = options.teleportId

    if (options.prompt) {
      // Spawn claude --remote with stdin inherited from the real TTY so claude
      // never detects a pipe and doesn't switch to --print mode.
      console.log('⏳ Starting Claude remote session...')
      const claudeOutput = await spawnClaudeRemote(options.prompt)
      const parsed = parseClaudeOutput(claudeOutput)
      viewUrl = viewUrl || parsed.viewUrl
      teleportId = teleportId || parsed.teleportId
    } else if (!process.stdin.isTTY) {
      console.log('⏳ Waiting for Claude session output...')
      const input = await readStdin()
      const parsed = parseClaudeOutput(input)
      viewUrl = viewUrl || parsed.viewUrl
      teleportId = teleportId || parsed.teleportId
    }

    // Create task if no task ID provided
    let actualTaskId = taskId
    if (!actualTaskId) {
      if (!options.title) {
        console.error('Task title required when creating a new task. Use --title "..."')
        process.exit(1)
      }

      console.log(`Creating task: ${options.title}`)
      const task = await api.createTask({
        projectId,
        title: options.title,
        description: options.description,
        subprojectId: options.subproject,
      })
      actualTaskId = task.id
      console.log(`✅ Created task: ${task.id}`)
    }

    // Update task with session info
    if (viewUrl || teleportId) {
      console.log('Registering session with task...')
      await api.updateTask(actualTaskId, {
        sessionViewUrl: viewUrl,
        sessionTeleportId: teleportId,
        status: 'DISPATCHED',
      })
      console.log('✅ Session registered')
    }

    // Output task ID for scripting
    console.log(`\nTask ID: ${actualTaskId}`)

    if (viewUrl) {
      console.log(`Session: ${viewUrl}`)
    }

    if (teleportId) {
      console.log(`Resume: claude --teleport ${teleportId}`)
    }
  } catch (error) {
    console.error(`Error: ${error}`)
    process.exit(1)
  }
}

/**
 * Run `claude --remote <prompt>` inside a PTY allocated by the `script` command
 * so claude sees a real TTY on all three stdio streams and never switches to
 * --print mode. Output is recorded to a temp file, then read back and parsed.
 *
 * `script` syntax differs between macOS and Linux:
 *   macOS: script -q <file> <cmd> [args...]
 *   Linux: script -q -c "<cmd> <args>" <file>
 */
function spawnClaudeRemote(prompt: string): Promise<string> {
  const tmpDir = mkdtempSync(join(tmpdir(), 'fd-claude-'))
  const tmpFile = join(tmpDir, 'output.txt')

  const isMac = process.platform === 'darwin'
  // Shell-escape the prompt for the Linux -c string
  const escaped = prompt.replaceAll("'", "'\"'\"'")
  const scriptArgs = isMac
    ? ['-q', tmpFile, 'claude', '--remote', prompt]
    : ['-q', '-c', `claude --remote '${escaped}'`, tmpFile]

  return new Promise((resolve, reject) => {
    const child = spawn('script', scriptArgs, {
      stdio: 'inherit', // claude sees real TTY on all streams
    })

    child.on('close', (code) => {
      try {
        // Read the recorded output (may contain ANSI escape sequences)
        let raw = ''
        try { raw = readFileSync(tmpFile, 'utf8') } catch { /* file may be empty on error */ }
        try { rmSync(tmpDir, { recursive: true }) } catch { /* best-effort cleanup */ }

        // Strip ANSI escape codes so the URL regex matches cleanly
        const output = raw
          .replace(/\x1b\[[0-9;]*[mGKHFJABCDr]/g, '')
          .replace(/\r/g, '')

        if (code !== 0) {
          reject(new Error(`claude exited with code ${code}\nOutput: ${output.slice(0, 500)}`))
        } else {
          resolve(output)
        }
      } catch (err) {
        reject(err)
      }
    })

    child.on('error', (err) => {
      reject(new Error(`Failed to spawn 'script' command: ${err.message}`))
    })
  })
}

function readStdin(): Promise<string> {
  return new Promise((resolve) => {
    let data = ''
    let resolved = false

    process.stdin.setEncoding('utf8')
    process.stdin.on('data', (chunk) => {
      data += chunk
    })
    process.stdin.on('end', () => {
      if (!resolved) {
        resolved = true
        resolve(data)
      }
    })
    process.stdin.on('close', () => {
      if (!resolved) {
        resolved = true
        resolve(data)
      }
    })

    // Wait up to 30 seconds for claude --remote to create a session
    // Most remote sessions are created within 5-10 seconds
    setTimeout(() => {
      if (!resolved) {
        resolved = true
        resolve(data)
      }
    }, 30000)
  })
}

function parseClaudeOutput(output: string): { viewUrl?: string; teleportId?: string } {
  const result: { viewUrl?: string; teleportId?: string } = {}

  // Parse view URL: "View: https://claude.ai/code/session_..."
  const viewMatch = output.match(/View:\s+(https:\/\/claude\.ai\/code\/session_[^\s]+)/)
  if (viewMatch) {
    result.viewUrl = viewMatch[1]
  }

  // Parse teleport ID: "Resume with: claude --teleport session_..."
  const teleportMatch = output.match(/--teleport\s+(session_[^\s]+)/)
  if (teleportMatch) {
    result.teleportId = teleportMatch[1]
  }

  return result
}

/**
 * Auto-detect project from current git repository.
 * Looks up the project by matching the repo's githubRepo field.
 */
async function autoDetectProject(api: FlightDeskAPI, activeOrg: OrganizationInfo): Promise<string> {
  const repoInfo = detectGitRepo()

  if (!repoInfo) {
    console.error('❌ Not in a git repository. Please provide a project ID.')
    console.error('   Usage: flightdesk register --project <project-id> [task-id]')
    process.exit(1)
  }

  console.log(`🔍 Detecting project from repository: ${repoInfo.remote}`)

  // Check if this repo is mapped to an organization
  const mappedOrg = getOrganizationByRepo(repoInfo.remote)
  if (!mappedOrg) {
    console.error(`❌ Repository "${repoInfo.remote}" is not mapped to any organization.`)
    console.error('   Run: flightdesk sync')
    console.error('   Or provide a project ID: flightdesk register --project <project-id> [task-id]')
    process.exit(1)
  }

  // Warn if mapped org differs from active org
  if (mappedOrg.id !== activeOrg.id) {
    console.error(`❌ Repository "${repoInfo.remote}" is mapped to organization "${mappedOrg.name}",`)
    console.error(`   but your active organization is "${activeOrg.name}".`)
    console.error('   Switch with: flightdesk org switch ' + mappedOrg.name)
    console.error('   Or provide a project ID: flightdesk register --project <project-id> [task-id]')
    process.exit(1)
  }

  // List projects and find matching one
  const projects = await api.listProjects()
  const matchingProjects = projects.filter(
    (p) => p.githubRepo?.toLowerCase() === repoInfo.remote.toLowerCase()
  )

  if (matchingProjects.length === 0) {
    console.error(`❌ No FlightDesk project found for repository "${repoInfo.remote}".`)
    console.error('   Available projects in this organization:')
    for (const p of projects) {
      console.error(`     - ${p.name} (${p.githubRepo || 'no repo'}) [${p.id}]`)
    }
    console.error('\n   Create a project or provide a project ID explicitly.')
    process.exit(1)
  }

  if (matchingProjects.length > 1) {
    console.error(`❌ Multiple projects found for repository "${repoInfo.remote}":`)
    for (const p of matchingProjects) {
      console.error(`     - ${p.name} [${p.id}]`)
    }
    console.error('\n   Please specify the project ID explicitly.')
    process.exit(1)
  }

  const project = matchingProjects[0]
  console.log(`✅ Found project: ${project.name} (${project.id})`)

  return project.id
}
